import {
  Component,
  ChangeDetectionStrategy,
  HostListener,
  ViewChild,
  ElementRef,
  inject,
  signal,
  computed,
  input,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { PaperIconComponent } from '@ricoh-paper/angular';
import { AdminTabsService, AdminTab } from '../../../../core/services/admin-tabs.service';
import { ADMIN_SECTIONS, AdminSection } from '../../../../core/services/admin-tabs.utils';
import { LocaleService } from '../../../../core/services/locale.service';
import { environment } from '../../../../../environments/environment';

/**
 * RESPONSIBILITY: Linear-style horizontal tab bar for the admin area.
 *   Pinned to the top of the viewport, full-width, glassmorphic.
 *
 * USED BY: AppShellComponent (mounted at the very top when on admin routes).
 *
 * SELF-CONTAINED: handles its own fixed positioning, z-index, and animations.
 *   Pass `placement="inline"` to render it as a flow element instead.
 *
 * AI CONTEXT:
 *   - Pure presentation. State lives in AdminTabsService.
 *   - Drag-to-reorder uses HTML5 drag API (no deps).
 *   - The toast is rendered as a small pill below the bar; auto-dismisses.
 *   - Designed to drop into a future Tauri shell with minimal change —
 *     swap fixed→relative when running inside a native title-bar region.
 */
@Component({
  selector: 'im-admin-tab-bar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PaperIconComponent, FormsModule, TranslocoPipe],
  host: {
    '[attr.data-placement]': 'placement()',
  },
  template: `
    <div class="bar" role="tablist" [attr.aria-label]="'settings.admin_tab_bar.open_admin_tabs' | transloco">
      <div class="strip" #strip>
        @for (tab of tabs(); track tab.id; let i = $index) {
          <button class="tab"
                  role="tab"
                  [class.active]="tab.id === activeId()"
                  [class.dirty]="tab.dirty"
                  [class.drag-source]="dragSourceIdx() === i"
                  [class.drop-before]="dropZone() === i"
                  [class.drop-after]="dropZone() === i + 1 && dragSourceIdx() !== i"
                  [attr.aria-selected]="tab.id === activeId()"
                  [attr.aria-label]="ariaLabel(tab)"
                  [title]="tab.titleKey ? (tab.titleKey | transloco) : tab.title"
                  draggable="true"
                  (click)="onClick(tab, $event)"
                  (auxclick)="onAuxClick(tab, $event)"
                  (contextmenu)="onContextMenu(tab, $event)"
                  (dragstart)="onDragStart($event, i)"
                  (dragover)="onDragOver($event, i)"
                  (drop)="onDrop($event, i)"
                  (dragend)="onDragEnd()">
            <span class="tab-icon" aria-hidden="true">
              <paper-icon [name]="tab.icon" [size]="13" />
            </span>
            <span class="tab-label">{{ tab.titleKey ? (tab.titleKey | transloco) : tab.title }}</span>
            @if (tab.dirty) {
              <span class="tab-indicator"
                    [attr.aria-label]="'settings.admin_tab_bar.unsaved_changes' | transloco"
                    [attr.title]="'settings.admin_tab_bar.unsaved_changes' | transloco"></span>
            }
            <span class="tab-close"
                  role="button"
                  tabindex="-1"
                  [attr.aria-label]="'settings.admin_tab_bar.close_tab' | transloco"
                  [attr.title]="'settings.admin_tab_bar.close_middle_click' | transloco"
                  (click)="onClose(tab, $event)">
              <paper-icon name="close" [size]="11" aria-hidden="true" />
            </span>
          </button>
        }

        <!-- Empty state — only in non-glass placements -->
        @if (!tabs().length && !isGlassCenter()) {
          <div class="empty">
            <paper-icon name="layout" [size]="13" aria-hidden="true" />
            <span>{{ 'settings.admin_tab_bar.empty_open_section_hint' | transloco }}</span>
          </div>
        }

        <!-- Inline + button keeps the strip self-contained in glass-center mode -->
        @if (isGlassCenter()) {
          <button #addBtn class="tab tab-add"
                  [attr.aria-label]="'settings.admin_tab_bar.open_new_tab' | transloco"
                  [attr.title]="'settings.admin_tab_bar.new_tab' | transloco"
                  [class.active]="pickerOpen()"
                  [disabled]="atCap()"
                  (click)="togglePicker($event)">
            <paper-icon name="plus" [size]="12" aria-hidden="true" />
          </button>
        }
      </div>

      @if (!isGlassCenter()) {
        <div class="bar-actions">
          @if (tabs().length > 0) {
            <span class="cap-counter" [attr.aria-label]="capLabel()">
              {{ tabs().length }}/{{ cap() }}
            </span>
          }
          <button class="action-btn"
                  [attr.aria-label]="'settings.admin_tab_bar.open_new_tab' | transloco"
                  [attr.title]="'settings.admin_tab_bar.new_tab' | transloco"
                  [disabled]="atCap()"
                  (click)="onAddClick()">
            <paper-icon name="plus" [size]="14" aria-hidden="true" />
          </button>
          <button class="action-btn"
                  [attr.aria-label]="'settings.admin_tab_bar.disable_admin_tabs' | transloco"
                  [attr.title]="'settings.admin_tab_bar.turn_off_tab_mode' | transloco"
                  (click)="onDisable()">
            <paper-icon name="close" [size]="13" aria-hidden="true" />
          </button>
        </div>
      }
    </div>

    <!-- Toast — non-modal feedback below the bar -->
    @if (toast(); as msg) {
      <div class="toast" role="status" aria-live="polite">
        <paper-icon name="alert-info" [size]="12" aria-hidden="true" />
        <span>{{ msg }}</span>
      </div>
    }

    <!-- Right-click context menu -->
    @if (contextMenu(); as ctx) {
      <div class="context-menu"
           role="menu"
           [style.left.px]="ctx.x"
           [style.top.px]="ctx.y"
           (click)="$event.stopPropagation()">
        <button class="ctx-item" role="menuitem" (click)="closeTab(ctx.tab)">
          <paper-icon name="close" [size]="12" aria-hidden="true" /> {{ 'settings.admin_tab_bar.ctx_close_tab' | transloco }}
        </button>
        <button class="ctx-item" role="menuitem"
                [disabled]="tabs().length < 2"
                (click)="closeOthers(ctx.tab)">
          {{ 'settings.admin_tab_bar.ctx_close_other_tabs' | transloco }}
        </button>
        <button class="ctx-item" role="menuitem"
                [disabled]="!hasTabsToRight(ctx.tab)"
                (click)="closeRight(ctx.tab)">
          {{ 'settings.admin_tab_bar.ctx_close_tabs_to_right' | transloco }}
        </button>
        <div class="ctx-divider" role="separator"></div>
        <button class="ctx-item" role="menuitem" (click)="copyUrl(ctx.tab)">
          {{ 'settings.admin_tab_bar.ctx_copy_tab_url' | transloco }}
        </button>
      </div>
    }

    <!-- New-tab picker — anchored to the (+) button via fixed position
         + viewport coordinates so it escapes the glass-top-bar clip. -->
    @if (pickerOpen()) {
      <div class="picker"
           role="dialog"
           [attr.aria-label]="'settings.admin_tab_bar.open_in_new_tab' | transloco"
           [style.top.px]="pickerAnchor()?.top ?? null"
           [style.left.px]="pickerAnchor()?.left ?? null"
           [class.picker--anchored]="pickerAnchor() !== null"
           (click)="$event.stopPropagation()">
        <div class="picker-header">
          <paper-icon name="search" [size]="13" aria-hidden="true" />
          <input class="picker-search"
                 type="text"
                 [attr.placeholder]="'settings.admin_tab_bar.open_in_new_tab_placeholder' | transloco"
                 [ngModel]="pickerQuery()"
                 (ngModelChange)="pickerQuery.set($event)"
                 (keydown)="onPickerKeydown($event)"
                 #pickerInput
                 [attr.aria-label]="'settings.admin_tab_bar.search_admin_sections' | transloco" />
          <kbd class="picker-kbd">esc</kbd>
        </div>
        <ul class="picker-list" role="listbox">
          @for (section of pickerResults(); track section.url; let i = $index) {
            <li role="option"
                class="picker-item"
                [class.focused]="pickerCursor() === i"
                [attr.aria-selected]="pickerCursor() === i"
                (mouseenter)="pickerCursor.set(i)"
                (click)="pickSection(section)">
              <span class="picker-icon" aria-hidden="true">
                <paper-icon [name]="section.icon" [size]="14" />
              </span>
              <span class="picker-text">
                <span class="picker-title">{{ (section.labelKey | transloco) || section.label }}</span>
                @if (section.descriptionKey || section.description) {
                  <span class="picker-desc">{{ (section.descriptionKey | transloco) || section.description }}</span>
                }
              </span>
              @if (alreadyOpen(section)) {
                <span class="picker-badge"
                      [attr.title]="'settings.admin_tab_bar.already_open_as_tab' | transloco">
                  {{ 'settings.admin_tab_bar.already_open' | transloco }}
                </span>
              }
            </li>
          }
          @if (!pickerResults().length) {
            <li class="picker-empty">{{ 'settings.admin_tab_bar.no_sections_match' | transloco:{ query: pickerQuery() } }}</li>
          }
        </ul>
        <div class="picker-footer">
          <span><kbd>↑</kbd><kbd>↓</kbd> {{ 'settings.admin_tab_bar.navigate' | transloco }}</span>
          <span><kbd>↵</kbd> {{ 'settings.admin_tab_bar.open' | transloco }}</span>
        </div>
      </div>
    }
  `,
  styleUrl: './admin-tab-bar.component.scss',
})
export class AdminTabBarComponent {
  private readonly service = inject(AdminTabsService);
  private readonly transloco = inject(TranslocoService);
  private readonly locale = inject(LocaleService);

  /** 'fixed' (default) pins the bar to the viewport top.
   *  'inline' renders it inline so a parent can position it.
   *  'glass-center' renders flat pills inside the existing glass-top-bar centre slot. */
  readonly placement = input<'fixed' | 'inline' | 'glass-center'>('fixed');

  protected readonly isGlassCenter = computed(() => this.placement() === 'glass-center');

  readonly tabs = this.service.tabs;
  readonly activeId = this.service.activeTabId;
  readonly toast = this.service.toastMessage;
  readonly cap = this.service.cap;
  readonly atCap = computed(() => this.tabs().length >= this.cap());
  readonly capLabel = computed(() => {
    this.locale.locale();
    return this.transloco.translate('settings.admin_tab_bar.cap_label', {
      count: this.tabs().length,
      cap: this.cap(),
    });
  });

  protected readonly dragSourceIdx = signal<number | null>(null);
  /** Index within the strip where a drop would land (0..tabs.length). */
  protected readonly dropZone = signal<number | null>(null);
  protected readonly contextMenu = signal<{ x: number; y: number; tab: AdminTab } | null>(null);

  // ── New-tab picker ────────────────────────────────────
  protected readonly pickerOpen = signal(false);
  protected readonly pickerQuery = signal('');
  protected readonly pickerCursor = signal(0);
  /** Viewport coordinates of the picker, computed from the trigger's
   *  bounding rect at open time. Needed because `.glass-top-bar` has
   *  `overflow: hidden` — an absolute-positioned picker is clipped.
   *  Fixed positioning lets it escape the clip. */
  protected readonly pickerAnchor = signal<{ top: number; left: number } | null>(null);
  @ViewChild('pickerInput') protected pickerInput?: ElementRef<HTMLInputElement>;
  @ViewChild('addBtn') protected addBtn?: ElementRef<HTMLButtonElement>;

  private readonly availableSections = computed<AdminSection[]>(() =>
    ADMIN_SECTIONS.filter(s => !s.devOnly || !environment.production),
  );

  protected readonly pickerResults = computed<AdminSection[]>(() => {
    this.locale.locale();
    const q = this.pickerQuery().trim().toLowerCase();
    const all = this.availableSections();
    if (!q) return all;
    return all.filter(s => {
      const label = s.labelKey ? this.transloco.translate(s.labelKey) : s.label;
      const desc = s.descriptionKey ? this.transloco.translate(s.descriptionKey) : (s.description ?? '');
      return label.toLowerCase().includes(q)
        || desc.toLowerCase().includes(q)
        || s.url.toLowerCase().includes(q)
        || s.label.toLowerCase().includes(q)
        || s.description?.toLowerCase().includes(q);
    });
  });

  /** Dismiss popovers on outside click. */
  @HostListener('document:click')
  protected onDocClick(): void {
    if (this.contextMenu()) this.contextMenu.set(null);
    if (this.pickerOpen()) this.closePicker();
  }

  protected ariaLabel(tab: AdminTab): string {
    this.locale.locale();
    const title = tab.titleKey ? this.transloco.translate(tab.titleKey) : tab.title;
    return tab.dirty
      ? this.transloco.translate('settings.admin_tab_bar.tab_aria_dirty', { title })
      : title;
  }

  // ── Tab interactions ─────────────────────────────────

  protected onClick(tab: AdminTab, event: MouseEvent): void {
    if ((event.target as HTMLElement).closest('.tab-close')) return;
    this.service.activate(tab.id);
  }

  protected onAuxClick(tab: AdminTab, event: MouseEvent): void {
    if (event.button === 1) {
      event.preventDefault();
      void this.service.close(tab.id);
    }
  }

  protected onContextMenu(tab: AdminTab, event: MouseEvent): void {
    event.preventDefault();
    this.contextMenu.set({ x: event.clientX, y: event.clientY, tab });
  }

  protected onClose(tab: AdminTab, event: MouseEvent): void {
    event.stopPropagation();
    void this.service.close(tab.id);
  }

  protected onAddClick(): void {
    this.togglePicker();
  }

  protected togglePicker(event?: MouseEvent): void {
    event?.stopPropagation();
    if (this.atCap()) return;
    if (this.pickerOpen()) {
      this.closePicker();
      return;
    }
    this.pickerQuery.set('');
    this.pickerCursor.set(0);
    // Anchor the picker to the (+) button's viewport coordinates so it
    // can escape the `overflow: hidden` clip on `.glass-top-bar`.
    const rect = this.addBtn?.nativeElement.getBoundingClientRect();
    if (rect) {
      this.pickerAnchor.set({
        top: rect.bottom + 6,
        left: rect.left + rect.width / 2,
      });
    } else {
      this.pickerAnchor.set(null);
    }
    this.pickerOpen.set(true);
    // Focus the input on next tick so the popover is in the DOM.
    queueMicrotask(() => this.pickerInput?.nativeElement.focus());
  }

  protected closePicker(): void {
    this.pickerOpen.set(false);
    this.pickerQuery.set('');
  }

  protected pickSection(section: AdminSection): void {
    this.closePicker();
    this.service.open(section.url, { newTab: true });
  }

  protected alreadyOpen(section: AdminSection): boolean {
    return this.tabs().some(t => t.url === section.url);
  }

  protected onPickerKeydown(event: KeyboardEvent): void {
    const max = this.pickerResults().length - 1;
    if (max < 0 && event.key !== 'Escape') return;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.pickerCursor.set(Math.min(this.pickerCursor() + 1, max));
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.pickerCursor.set(Math.max(this.pickerCursor() - 1, 0));
        break;
      case 'Enter': {
        event.preventDefault();
        const pick = this.pickerResults()[this.pickerCursor()];
        if (pick) this.pickSection(pick);
        break;
      }
      case 'Escape':
        event.preventDefault();
        this.closePicker();
        break;
    }
  }

  protected onDisable(): void {
    this.service.setEnabled(false);
  }

  // ── Context menu ─────────────────────────────────────

  protected closeTab(tab: AdminTab): void { this.contextMenu.set(null); void this.service.close(tab.id); }
  protected closeOthers(tab: AdminTab): void { this.contextMenu.set(null); void this.service.closeOthers(tab.id); }
  protected closeRight(tab: AdminTab): void { this.contextMenu.set(null); void this.service.closeToRight(tab.id); }

  protected hasTabsToRight(tab: AdminTab): boolean {
    const list = this.tabs();
    const idx = list.findIndex(t => t.id === tab.id);
    return idx >= 0 && idx < list.length - 1;
  }

  protected copyUrl(tab: AdminTab): void {
    this.contextMenu.set(null);
    try {
      const url = `${window.location.origin}${window.location.pathname}#${tab.url}`;
      void navigator.clipboard.writeText(url);
      this.service.flashToast(this.transloco.translate('settings.admin_tab_bar.toast_url_copied'));
    } catch {
      /* clipboard may be unavailable */
    }
  }

  // ── Drag-to-reorder ──────────────────────────────────

  protected onDragStart(event: DragEvent, idx: number): void {
    this.dragSourceIdx.set(idx);
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      // Required for Firefox to begin a drag operation.
      event.dataTransfer.setData('text/plain', String(idx));
    }
  }

  protected onDragOver(event: DragEvent, idx: number): void {
    if (this.dragSourceIdx() === null) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
    // Decide whether the drop should land before or after this tab based on
    // pointer X position relative to the tab's centre.
    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    const before = (event.clientX - rect.left) < rect.width / 2;
    this.dropZone.set(before ? idx : idx + 1);
  }

  protected onDrop(event: DragEvent, _targetIdx: number): void {
    event.preventDefault();
    const source = this.dragSourceIdx();
    const dropAt = this.dropZone();
    if (source === null || dropAt === null) {
      this.cleanupDrag();
      return;
    }
    // Account for the source being removed before insertion when moving right.
    let insertAt = dropAt;
    if (insertAt > source) insertAt -= 1;
    if (insertAt !== source) this.service.reorder(source, insertAt);
    this.cleanupDrag();
  }

  protected onDragEnd(): void {
    this.cleanupDrag();
  }

  private cleanupDrag(): void {
    this.dragSourceIdx.set(null);
    this.dropZone.set(null);
  }
}
